import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';
import { Dialog, DialogContent } from '../components/ui/Dialog';
import { Karti } from '../components/brand/Karti';
import { PhaseDots } from '../components/brand/PhaseBar';
import { PHASE_COLORS } from '../lib/utils';
import apiClient from '../lib/api/client';

interface PublicStats {
  totalCourses: number;
  totalVocabularies: number;
  totalUsers: number;
}

interface TrendingCourse {
  id: number;
  name: string;
  memberCount: number;
  vocabularyCount: number;
  ownerName?: string | null;
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/voc7/id6758789767';

const phases = [
  { phase: 1, score: '0-1', title: 'Neue Wörter', description: 'Vokabeln, die du noch nicht kennst. Werden am häufigsten abgefragt.' },
  { phase: 2, score: '2-3', title: 'Anfänger', description: 'Du beginnst dich zu erinnern. Regelmäßige Wiederholung nötig.' },
  { phase: 3, score: '4-6', title: 'Fortgeschritten', description: 'Die Wörter sitzen schon besser. Gelegentliche Wiederholung.' },
  { phase: 4, score: '7-10', title: 'Solide', description: 'Diese Wörter sitzen schon recht gut. Moderate Wiederholung.' },
  { phase: 5, score: '11-15', title: 'Gut gelernt', description: 'Du beherrschst diese Vokabeln zuverlässig.' },
  { phase: 6, score: '16-21', title: 'Sehr gut', description: 'Fast perfekt beherrscht. Nur gelegentliche Auffrischung.' },
  { phase: 7, score: '22+', title: 'Meisterschaft', description: 'Perfekt im Langzeitgedächtnis verankert.' },
];

const features = [
  { title: 'Kursverwaltung', description: 'Erstelle eigene Kurse oder trete öffentlichen Kursen bei und lerne gemeinsam mit anderen.' },
  { title: 'Bulk-Import', description: 'Importiere bis zu 500 Vokabeln auf einmal. Einfach per Copy & Paste.' },
  { title: '3 Quiz-Modi', description: 'Flashcards, Multiple-Choice oder Test-Modus - wähle den passenden Lernstil.' },
  { title: 'Detaillierte Statistiken', description: 'Verfolge deinen Fortschritt mit Diagrammen und Analysen über deinen Lernerfolg.' },
  { title: 'Adaptiver Algorithmus', description: 'Das System lernt mit dir und passt sich automatisch an deine Stärken an.' },
  { title: 'Kollaboration', description: 'Teile Kurse mit Freunden und arbeitet zusammen an Vokabellisten.' },
];

const steps = [
  { title: 'App herunterladen', description: 'Lade die VOC7-App aus dem App Store oder Google Play Store herunter.' },
  { title: 'Registrieren und loslegen', description: 'Erstelle dein Konto direkt in der App und beginne mit dem Lernen.' },
  { title: 'Moderne Features nutzen', description: 'Genieße alle neuen Features, die nur in der mobilen App verfügbar sind.' },
  { title: 'Überall lernen', description: 'Nutze die mobile App unterwegs, offline und mit Push-Benachrichtigungen.' },
  { title: 'Bessere Erfahrung', description: 'Erlebe eine optimierte, schnellere und intuitivere Benutzeroberfläche.' },
];

function AppStoreBadge() {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Laden im App Store"
      className="inline-block p-1 transition-opacity hover:opacity-80"
    >
      <img src="/app-store-badge.svg" alt="Laden im App Store" className="h-14 w-auto" />
    </a>
  );
}

interface CoursePreview {
  id: number;
  name: string;
  memberCount: number;
  vocabularyCount: number;
  ownerName: string | null;
  sampleVocabularies: { id: number; question: string; answer: string }[];
}

function CoursePreviewDialog({
  course,
  onClose,
}: {
  course: TrendingCourse | null;
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<CoursePreview | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setPreview(null);
    setFailed(false);
    if (!course) return;
    let cancelled = false;
    fetch(`/api/public/courses/${course.id}/preview`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data) => !cancelled && setPreview(data))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [course]);

  const samples = preview?.sampleVocabularies ?? [];

  return (
    <Dialog open={course !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        {course && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                  Vorschau · von {(preview?.ownerName ?? course.ownerName ?? 'Öffentlich').toUpperCase()}
                </p>
                <h3 className="text-2xl font-extrabold tracking-tight">{course.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {course.vocabularyCount} Vokabeln · {course.memberCount} Mitglieder
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={onClose} aria-label="Schließen">
                ✕
              </Button>
            </div>

            {failed ? (
              <p className="flex items-center gap-2 rounded-card border-[2.5px] border-ink bg-paper p-3 text-sm font-semibold">
                <span className="h-3 w-3 rounded-full bg-wrong" /> Vorschau konnte nicht geladen werden.
              </p>
            ) : !preview ? (
              <div className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-6 animate-pulse rounded-full bg-track" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.08em]">
                  Reinschauen · {samples.length} von {preview.vocabularyCount}
                </p>
                <ul className="overflow-hidden rounded-panel border-[2.5px] border-ink bg-paper shadow-hard-sm">
                  {samples.map((v, i) => (
                    <li
                      key={v.id}
                      className={`flex items-baseline justify-between gap-4 px-4 py-3 ${i > 0 ? 'border-t-[1.5px] border-divider' : ''}`}
                    >
                      <span className="font-bold">{v.question}</span>
                      <span className="text-right text-muted-foreground">{v.answer}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col items-center gap-1 pt-2 text-center">
              <p className="text-sm text-muted-foreground">Zum Lernen und Beitreten brauchst du die App.</p>
              <AppStoreBadge />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function LandingPage() {
  const [previewCourse, setPreviewCourse] = useState<TrendingCourse | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated, user, clearAuth } = useAuthStore();
  const [stats, setStats] = useState<PublicStats | null>(null);
  const [trendingCourses, setTrendingCourses] = useState<TrendingCourse[]>([]);

  useEffect(() => {
    // Fetch public stats
    fetch('/api/public/stats')
      .then((res) => res.json())
      .then(setStats)
      .catch(console.error);

    // Fetch trending courses
    fetch('/api/public/trending-courses')
      .then((res) => res.json())
      .then(setTrendingCourses)
      .catch(console.error);
  }, []);

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      window.location.reload();
    }
  };

  const cta = isAuthenticated ? (
    <Button variant="accent" size="lg" onClick={() => navigate('/app/dashboard')}>
      Zum Dashboard →
    </Button>
  ) : (
    <AppStoreBadge />
  );

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <div className="border-b-[2.5px] border-ink bg-paper">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 -rotate-3 items-center justify-center rounded-tile border-2 border-ink bg-marker font-extrabold shadow-hard-sm">
              V
            </div>
            <span className="text-xl font-extrabold tracking-tight">Voc7</span>
          </div>
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <span className="hidden text-sm font-bold sm:inline">{user?.displayname || user?.nickname}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Abmelden
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 pb-10 pt-10 md:pt-16">
        {/* Hero */}
        <section className="mb-20 grid items-center gap-10 md:mb-28 md:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <span className="inline-block -rotate-2 rounded-chip border-2 border-ink bg-marker px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.07em] shadow-hard-sm">
              Nur noch in der App verfügbar
            </span>
            <h1 className="text-[44px] font-extrabold leading-[1] tracking-[-0.035em] sm:text-6xl lg:text-7xl">
              Vokabeln lernen in <span className="highlight">7 Phasen.</span>
            </h1>
            <p className="max-w-xl text-lg leading-relaxed text-body">
              Die Zukunft des Vokabellernens liegt in der mobilen App. Alle neuen Features und
              Updates gibt es nur noch dort.
            </p>
            <div className="pt-1">{cta}</div>
          </div>

          {/* Mock flashcard + Karti */}
          <div className="relative mx-auto h-[320px] w-full max-w-[300px]">
            <div className="absolute inset-x-3 top-6 h-[270px] rotate-[5deg] rounded-flash border-[3px] border-ink bg-marker shadow-hard-6" />
            <div className="absolute inset-x-0 top-0 flex h-[280px] -rotate-2 flex-col justify-between rounded-flash border-[3px] border-ink bg-cream p-5 shadow-hard-6">
              <span className="label-mono">Japanisch → Deutsch</span>
              <div className="text-center">
                <div className="font-term text-[96px] font-black leading-none">水</div>
                <div className="mt-2 font-mono text-base text-muted-foreground">mizu</div>
              </div>
              <span className="text-sm text-muted-foreground">Tippen zum Umdrehen</span>
            </div>
            <Karti size={84} mood="happy" className="absolute -bottom-2 -right-3 rotate-6" />
          </div>
        </section>

        {/* Stats */}
        {stats && (
          <section className="mb-20 grid grid-cols-1 gap-4 sm:grid-cols-3 md:mb-28">
            <StatCard value={stats.totalCourses} label="Öffentliche Kurse" />
            <StatCard value={stats.totalVocabularies.toLocaleString()} label="Vokabeln" />
            <StatCard value={stats.totalUsers} label="Aktive Lerner" />
          </section>
        )}

        {/* 7-Phasen-System */}
        <section className="mb-20 space-y-8 md:mb-28">
          <SectionHead
            title="Das 7-Phasen-Lernsystem"
            text="Ein intelligenter Algorithmus passt sich an deinen individuellen Fortschritt an und führt dich Schritt für Schritt vom Anfänger zum Experten"
          />
          <div className="flex h-5 overflow-hidden rounded-[7px] border-2.5 border-ink" aria-hidden>
            {PHASE_COLORS.map((c, i) => (
              <div key={c} className="flex-1" style={{ background: c, borderLeft: i ? '1px solid rgba(22,21,18,.25)' : undefined }} />
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {phases.map((p) => (
              <div key={p.phase} className="ink-card h-full p-4">
                <div className="mb-3 flex items-center gap-3">
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-tile border-2 border-ink font-mono text-lg font-bold"
                    style={{ background: PHASE_COLORS[p.phase - 1], color: p.phase >= 6 ? '#F3EFE6' : '#161512' }}
                  >
                    {p.phase}
                  </div>
                  <div>
                    <h3 className="text-[17px] font-bold leading-tight">{p.title}</h3>
                    <span className="label-mono !text-[10px]">Score {p.score}</span>
                  </div>
                </div>
                <PhaseDots phase={p.phase} className="mb-3" />
                <p className="text-sm leading-relaxed text-body">{p.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="mb-20 space-y-8 md:mb-28">
          <SectionHead title="Alle Features im Überblick" text="Alles was du brauchst, um effektiv Vokabeln zu lernen" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <div key={f.title} className="ink-card h-full p-5">
                <span className="label-mono mb-3 block">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mb-2 text-[22px] font-bold leading-tight">{f.title}</h3>
                <p className="text-sm leading-relaxed text-body">{f.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mx-auto mb-20 max-w-3xl space-y-8 md:mb-28">
          <SectionHead title="So geht's in der App" text="Lade dir die mobile App herunter für die beste Erfahrung" />
          <div className="space-y-3">
            {steps.map((s, i) => (
              <div key={s.title} className="ink-card flex gap-4 p-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-tile border-2 border-ink font-mono text-base font-bold ${
                    i === 0 ? 'bg-marker' : 'bg-paper'
                  }`}
                >
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-[17px] font-bold">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-body">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Trending Courses */}
        {trendingCourses.length > 0 && (
          <section className="mb-20 space-y-8 md:mb-28">
            <SectionHead title="Beliebte Kurse" text="Lerne gemeinsam mit der Community" />
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {trendingCourses.map((course) => (
                <button
                  key={course.id}
                  className="ink-card press p-4 text-left"
                  onClick={() => (isAuthenticated ? navigate(`/app/courses/${course.id}`) : setPreviewCourse(course))}
                >
                  <h3 className="mb-2 text-[17px] font-bold">{course.name}</h3>
                  <p className="text-[13px] text-muted-foreground">
                    {course.vocabularyCount} Vokabeln · {course.memberCount} Mitglieder
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="mx-auto max-w-3xl">
          <div className="rounded-hero border-[3px] border-ink bg-ink p-8 text-center shadow-hard-ink md:p-12">
            <h2 className="mb-4 text-4xl font-extrabold tracking-tight text-paper md:text-5xl">
              Lade dir die <span className="text-marker">App</span> herunter!
            </h2>
            <p className="mx-auto mb-8 max-w-xl text-lg text-on-ink-sub">
              {isAuthenticated
                ? 'Du kannst das Web-Dashboard noch nutzen, aber die beste Erfahrung wartet in der App!'
                : 'Die Zukunft des Lernens ist mobil. Hol dir alle Features und Updates in der VOC7-App.'}
            </p>
            <div className="flex justify-center">
              {isAuthenticated ? (
                <Button variant="accent" size="lg" onClick={() => navigate('/app/dashboard')}>
                  Zum Dashboard →
                </Button>
              ) : (
                <AppStoreBadge />
              )}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="mx-auto mt-16 max-w-4xl pb-6 text-center">
          <div className="flex items-center justify-center gap-5 text-sm font-bold">
            <Link to="/datenschutz" className="underline decoration-marker decoration-2 underline-offset-4">
              Datenschutz
            </Link>
            <Link to="/support" className="underline decoration-marker decoration-2 underline-offset-4">
              Support
            </Link>
            <a href="mailto:support@n-mayr.net" className="underline decoration-marker decoration-2 underline-offset-4">
              Kontakt
            </a>
          </div>
          <p className="label-mono mt-4 !text-[10px]">Entwickelt von Julian und Noel</p>
        </footer>
      </div>
      <CoursePreviewDialog course={previewCourse} onClose={() => setPreviewCourse(null)} />
    </div>
  );
}

function SectionHead({ title, text }: { title: string; text: string }) {
  return (
    <div className="max-w-2xl space-y-3">
      <h2 className="text-[32px] font-extrabold leading-[1.05] tracking-[-0.03em] md:text-5xl">{title}</h2>
      <p className="text-lg text-body">{text}</p>
    </div>
  );
}

function StatCard({ value, label }: { value: number | string; label: string }) {
  return (
    <div className="ink-card p-5">
      <div className="text-5xl font-extrabold leading-none tracking-[-0.04em]">{value}</div>
      <div className="label-mono mt-3">{label}</div>
    </div>
  );
}
